import { computed, makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { WebsocketV1 } from 'src/AppState/websocketV1/WebsocketV1';
import { NotificationSelfExclusionChannelType, SelfExclusionNotificationType } from 'src_server/types/notificationData';
import {
    getSelfExclusionNotificationEntity,
    isSelfExclusionNotification,
    SELF_EXCLUSION_CHANNEL,
} from './selfExclusionNotificationUtils';

type SelfExclusionNotificationActionType = 'assign' | 'unassign' | 'close';

export class SelfExcludedTabState {
    private readonly notificationsResource: Resource<NotificationSelfExclusionChannelType>;
    private readonly webSocketV1: WebsocketV1;

    public static get = AutoWeakMap.create((common: Common) => new SelfExcludedTabState(common));

    private constructor(private readonly common: Common) {
        this.webSocketV1 = WebsocketV1.get(common);

        this.notificationsResource = new Resource(async (): Promise<NotificationSelfExclusionChannelType> => {
            const notifications = await common.trpcClient.client.notification.getSelfExclusionNotifications.query();
            return notifications;
        });

        const refresh = (): void => {
            this.refresh().catch((error) => {
                console.error('SelfExcludedTabState refresh error', error);
            });
        };

        this.webSocketV1.subscribe(`S:Notification:${SELF_EXCLUSION_CHANNEL}`, refresh);
        makeAutoObservable(this);
    }

    @computed private get userId(): number | undefined {
        return this.common.session.userId ?? undefined;
    }

    @computed public get isLoading(): boolean {
        return this.notificationsResource.get().type === 'loading';
    }

    @computed public get hasError(): boolean {
        return this.notificationsResource.get().type === 'error';
    }

    @computed public get visibleNotifications(): Array<SelfExclusionNotificationType> {
        const notifications = this.notificationsResource.getReady();
        if (notifications === null) {
            return [];
        }

        const selfExclusionNotifications: Array<SelfExclusionNotificationType> = [];
        for (const notification of notifications) {
            if (isSelfExclusionNotification(notification)) {
                selfExclusionNotifications.push(notification);
            }
        }

        return selfExclusionNotifications;
    }

    @computed public get yourNotifications(): Array<SelfExclusionNotificationType> {
        const userId = this.userId;
        if (userId === undefined) {
            return [];
        }

        return this.visibleNotifications.filter((notification) => notification.assignee?.id === userId);
    }

    @computed public get noneNotifications(): Array<SelfExclusionNotificationType> {
        return this.visibleNotifications.filter(
            (notification) => notification.assignee?.id == null && notification.status !== 'assigned'
        );
    }

    @computed public get otherNotifications(): Array<SelfExclusionNotificationType> {
        const userId = this.userId;
        if (userId === undefined) {
            return [];
        }

        return this.visibleNotifications.filter((notification) => {
            const assigneeId = notification.assignee?.id;
            return assigneeId !== undefined && assigneeId !== userId;
        });
    }

    public refresh = async (): Promise<void> => {
        await this.notificationsResource.refresh();
    };

    public updateNotification = async (
        notification: SelfExclusionNotificationType,
        action: SelfExclusionNotificationActionType
    ): Promise<void> => {
        const response = await this.common.trpcClient.client.notificationCounts.updateNotificationCounts.mutate({
            entity: getSelfExclusionNotificationEntity(notification),
            action,
            id: notification.id,
        });

        if (response.responseStatus !== 'success') {
            console.error('SelfExcludedTabState updateNotification error', response);
        }

        await this.refresh();
    };
}
